import {ApplicationInsights} from '@microsoft/applicationinsights-web';
import env from '../../env.json';
import {store} from '../../App';
import {getAIConfigFromRemote, fetchAndCacheRemoteConfig} from './remoteConfig';
import {DEFAULT_AI_CONFIG, AIConfig} from './config';

const hasConnectionString = Boolean(env.AZURE_APPINSIGHT_CONNECTION_STRING);

// Stores dynamically loaded config (updated by initializeApplicationInsights)
let aiConfig: AIConfig = DEFAULT_AI_CONFIG;

// Bootstrapped with DEFAULT_AI_CONFIG. Will be updated on startup by initializeApplicationInsights().
export const appInsights = new ApplicationInsights({
  config: {
    connectionString: env.AZURE_APPINSIGHT_CONNECTION_STRING,
    samplingPercentage: DEFAULT_AI_CONFIG.samplingPercentage,
    enableAutoRouteTracking: DEFAULT_AI_CONFIG.enableAutoRouteTracking,
    disableExceptionTracking: DEFAULT_AI_CONFIG.disableExceptionTracking,
    disableFetchTracking: DEFAULT_AI_CONFIG.disableFetchTracking,
    disableAjaxTracking: DEFAULT_AI_CONFIG.disableAjaxTracking,
    disableTelemetry: DEFAULT_AI_CONFIG.disableTelemetry,
    maxBatchSizeInBytes: DEFAULT_AI_CONFIG.maxBatchSizeInBytes,
    maxBatchInterval: DEFAULT_AI_CONFIG.maxBatchInterval,
    loggingLevelTelemetry: env.AZURE_APPINSIGHT_LOGGING_LEVEL_TELEMETRY ?? DEFAULT_AI_CONFIG.loggingLevelTelemetry,
  },
});

/**
 * Initializes Application Insights with Firebase Remote Config.
 * Must be called once during app startup, after Firebase Remote Config has been fetched.
 * If not called, the appInsights instance continues to use DEFAULT_AI_CONFIG.
 */
export const initializeApplicationInsights = async (): Promise<void> => {
  if (!hasConnectionString) {
    console.log('[AppInsights] No connection string found, skipping remote config init');
    return;
  }
  try {
    await fetchAndCacheRemoteConfig();
    aiConfig = await getAIConfigFromRemote();
    if (appInsights?.config) {
      appInsights.config.samplingPercentage = aiConfig.samplingPercentage;
      appInsights.config.disableFetchTracking = aiConfig.disableFetchTracking;
      appInsights.config.disableAjaxTracking = aiConfig.disableAjaxTracking;
      appInsights.config.disableTelemetry = aiConfig.disableTelemetry;
      appInsights.config.maxBatchSizeInBytes = aiConfig.maxBatchSizeInBytes;
      appInsights.config.maxBatchInterval = aiConfig.maxBatchInterval;
    }
  } catch (error) {
    console.error('[AppInsights] Error initializing with remote config:', error);
  }
};

/**
 * Returns the currently active Application Insights configuration.
 * Call after initializeApplicationInsights() to get the live values.
 */
export const getAIConfig = (): AIConfig => aiConfig;

export const trackInsightPageView = (screenName: any, params?: any) => {
  const userDetails: any = store?.getState()?.appState?.userInfo;

  appInsights.trackPageView({
    name: screenName,
    properties: {
      name: userDetails?.name,
      staffPositionId: userDetails?.staffPositionId,
      userId: userDetails?.userId,
      environment: `Admin ${env.ENVIRONMENT}`,
      ...params,
    },
  });
};

export const trackInsightEvent = (eventName: any, params?: any) => {
  const userDetails: any = store?.getState()?.appState?.userInfo;

  appInsights.trackEvent({
    name: eventName,
    properties: {
      name: userDetails?.name,
      staffPositionId: userDetails?.staffPositionId,
      userId: userDetails?.userId,
      environment: `Admin ${env.ENVIRONMENT}`,
      ...params,
    },
  });
};

export const trackInsightTrace = (message: any, params?: any) => {
  const userDetails: any = store?.getState()?.appState?.userInfo;

  appInsights.trackTrace({
    message: message,
    severityLevel: (env as any).severityLevel ?? 1,
    properties: {
      name: userDetails?.name,
      staffPositionId: userDetails?.staffPositionId,
      userId: userDetails?.userId,
      environment: `Admin ${env.ENVIRONMENT}`,
      ...params,
    },
  });
};

export const trackInsightException = (error: any, params?: any) => {
  const userDetails: any = store?.getState()?.appState?.userInfo;

  appInsights.trackException({
    exception: error,
    properties: {
      name: userDetails?.name,
      staffPositionId: userDetails?.staffPositionId,
      userId: userDetails?.userId,
      environment: `Admin ${env.ENVIRONMENT}`,
      ...params,
    },
  });
};

export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  let d = new Date().getTime();
  let d2 = 0;
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = Math.random() * 16;
    if (d > 0) {
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
